// Copyright (c) 2024 Visual Intelligence LP. All rights reserved.
//
import { observable, action, makeObservable } from 'mobx';
import { injectable } from 'inversify';

import ITwinsService from '@shared/services/itwins';
import container from '@core/di';
import { ITwinsProjectDTO } from '@shared/models/itwins';
import { TOKENS_KEYS } from '@shared/constants/common';
import { RepoTokenDTO } from '@shared/models/document-repo';

@injectable()
export default class ITwinsStore {
  static diToken = Symbol('itwin-store');
  private service = container.get<ITwinsService>(ITwinsService.diToken);

  @observable _token: RepoTokenDTO | undefined;

  constructor() {
    makeObservable(this);
    this._token = undefined;
  }

  get token(): RepoTokenDTO | undefined {
    if(this._token) {
      // TBD: check if expired and refresh here.
      console.log("*** token from props:", this._token)
      return this._token;
    }
    else {
      // try to load the last used token from the browser local storage.
      const jsonStr = sessionStorage.getItem(TOKENS_KEYS.itwinsLogin);
      try {
        if(jsonStr) {
          const tokenObj = JSON.parse(jsonStr) as RepoTokenDTO;
          if(tokenObj){
            // TBD: check if expired and refresh here.
            console.log("*** token from localstorage:", tokenObj)
            return tokenObj;
          }          
        }    
        else {
          console.log("*** Could not read token from local storage.");
          return undefined;
        }    
      }
      catch(err) {
        console.error(`***Could not parse token from browser storage: ${jsonStr}`);
      }

      return undefined;
    }
  }

  // Call reresponding funcs in the service module.

  @action getToken = async (code: string): Promise<RepoTokenDTO> => {
    try {
      const token = await this.service.getToken(code);

      if(token) {        // store token in browser storage.
        token.authCode = code;
        sessionStorage.setItem(TOKENS_KEYS.itwinsLogin, JSON.stringify(token));
        this._token = token;
        
        console.log("*** Store token in props and browser:", sessionStorage.getItem(TOKENS_KEYS.itwinsLogin));
      }

      return token;
    } 
    catch (err) {
      throw err;
    }
  }

  @action renewToken = async (refresh_token: string): Promise<RepoTokenDTO> => {
    try {
      const token = await this.service.renewToken(refresh_token);

      if(token) {        // store token in browser storage.
        sessionStorage.setItem(TOKENS_KEYS.itwinsLogin, JSON.stringify(token));
        this._token = token;
        
        console.log("*** Store renewed token in props and browser:", sessionStorage.getItem(TOKENS_KEYS.itwinsLogin));
      }

      return token;
    } 
    catch (err) {
      throw err;
    }
  }

  getTowers = async (): Promise<Array<ITwinsProjectDTO>> => {
    try {
      const towers = await this.service.getTowers();

      return towers;
    } 
    catch (err) {
      throw err;
    }
  }

  getProjects = async (): Promise<Array<ITwinsProjectDTO>> => {
    try {
      const towers = await this.service.getProjects();

      return towers;
    } 
    catch (err) {
      throw err;
    }
  }
}